import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { CloudUpload, DeleteOutline, Search, Visibility } from '@material-ui/icons';
import PartnerMainContainer from '../../../components/PartnerMainContainer';
import PartnerService from '../../../services/PartnerService';
import Message from '../../tools/Message';
import TitlePartner from '../component/Title';
import ExcelViewer from './ExcelView';
import ImportDialog from './ImportDialog';

const IMPORT_STATUS_LABELS = {
  UPLOADED: 'Fichier reçu',
  READY_FOR_SYNC: 'Prêt à synchroniser',
  SYNCED: 'Synchronisé',
};

const isSyncedPeriod = (value) => value === true || value === 1 || value === '1';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('fr-FR');
};

const PartnerImportView = () => {
  const [periods, setPeriods] = useState([]);
  const [fileNameFilter, setFileNameFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [periodToReplace, setPeriodToReplace] = useState(null);
  const [viewPeriod, setViewPeriod] = useState(null);
  const [viewFile, setViewFile] = useState(null);
  const [periodToRemove, setPeriodToRemove] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState({ open: false, color: 'green', messageInfo: '' });

  const loadPeriods = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await PartnerService.getMyPeriods({ file_name: fileNameFilter.trim() });
      setPeriods(result?.status
        ? (result.data || []).filter((period) => ['READY_FOR_SYNC', 'SYNCED'].includes(period.import_status))
        : []);
    } finally {
      setIsLoading(false);
    }
  }, [fileNameFilter]);

  useEffect(() => {
    const timer = setTimeout(loadPeriods, 250);
    return () => clearTimeout(timer);
  }, [loadPeriods]);

  const showError = (text) => setMessage({ open: true, color: 'red', messageInfo: text });

  const handleView = async (period) => {
    setBusyId(period.part_period_id);
    try {
      const file = await PartnerService.getPeriodFile(period.part_period_id, period.file_name);
      setViewPeriod(period);
      setViewFile(file);
    } catch (error) {
      showError('Impossible de lire le fichier importé.');
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async () => {
    if (!periodToRemove) return;
    setBusyId(periodToRemove.part_period_id);
    const result = await PartnerService.removePeriod(periodToRemove.part_period_id);
    if (result?.status) {
      setPeriodToRemove(null);
      setMessage({ open: true, color: 'green', messageInfo: result.message || 'Import retiré.' });
      await loadPeriods();
      window.dispatchEvent(new Event('partner:refresh-unsynced-periods'));
    } else {
      showError(result?.message || 'Impossible de retirer cet import.');
    }
    setBusyId(null);
  };

  return (
    <PartnerMainContainer>
      <Message
        open={message.open}
        color={message.color}
        messageInfo={message.messageInfo}
        handleClose={() => setMessage((current) => ({ ...current, open: false }))}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', marginBottom: 20 }}>
        <TitlePartner title="Importer des données" sub={`(${periods.length})`} />
        <Button variant="contained" color="primary" startIcon={<CloudUpload />} onClick={() => { setPeriodToReplace(null); setImportOpen(true); }}>
          Importer
        </Button>
      </div>

      <Paper style={{ padding: 16 }}>
        <TextField
          value={fileNameFilter}
          onChange={(event) => setFileNameFilter(event.target.value)}
          variant="outlined"
          size="small"
          placeholder="Filtrer par nom de fichier"
          style={{ width: 360, maxWidth: '100%', marginBottom: 16 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
        />

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nom fichier</TableCell>
                <TableCell>Date import</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={24} /></TableCell></TableRow>
              ) : periods.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center">Aucun import trouvé.</TableCell></TableRow>
              ) : periods.map((period) => {
                const synced = isSyncedPeriod(period.is_sync);
                const busy = Number(busyId) === Number(period.part_period_id);
                const syncing = period.import_status === 'SYNCING';
                return (
                  <TableRow key={period.part_period_id} hover>
                    <TableCell>
                      <Typography variant="body2"><strong>{period.file_name || '-'}</strong></Typography>
                      <Typography variant="caption" color="textSecondary">{period.part_period_label || '-'}</Typography>
                    </TableCell>
                    <TableCell>{formatDate(period.created_at)}</TableCell>
                    <TableCell>{synced ? 'Synchronisé' : (IMPORT_STATUS_LABELS[period.import_status] || 'Statut inconnu')}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Voir fichier et informations">
                        <span><IconButton size="small" disabled={busy} onClick={() => handleView(period)}><Visibility /></IconButton></span>
                      </Tooltip>
                      {/* {!synced && !syncing && ['FAILED', 'ACTION_REQUIRED'].includes(period.import_status) && (
                        <Tooltip title="Remplacer le fichier corrigé">
                          <span><IconButton size="small" disabled={busy} onClick={() => { setPeriodToReplace(period); setImportOpen(true); }}><CloudUpload /></IconButton></span>
                        </Tooltip>
                      )} */}
                      {!synced && !syncing && (
                        <Tooltip title="Retirer import non synchronisé">
                          <span><IconButton size="small" disabled={busy} onClick={() => setPeriodToRemove(period)}><DeleteOutline /></IconButton></span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <ImportDialog
        openDialog={importOpen}
        initialPeriod={periodToReplace}
        onClose={() => { setImportOpen(false); setPeriodToReplace(null); }}
        onImported={loadPeriods}
      />
      <ExcelViewer
        open={Boolean(viewFile)}
        file={viewFile}
        period={viewPeriod}
        onClose={() => { setViewFile(null); setViewPeriod(null); }}
      />

      <Dialog open={Boolean(periodToRemove)} onClose={() => busyId === null && setPeriodToRemove(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Annuler cet import ?</DialogTitle>
        <DialogContent dividers>
          <Typography>Fichier <strong>{periodToRemove?.file_name}</strong>, staging et période seront supprimés.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPeriodToRemove(null)} disabled={busyId !== null}>Conserver</Button>
          <Button color="secondary" variant="contained" onClick={handleRemove} disabled={busyId !== null}>Retirer</Button>
        </DialogActions>
      </Dialog>
    </PartnerMainContainer>
  );
};

export default PartnerImportView;
